import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';

// ── Types ──

export interface Product {
  id: number;
  name: string;
  code: string;
  price: number;
  priceVAT: number;
  weight: number;
  unit: string;
  category: string;
}

export interface SelectedItem {
  name: string;
  code: string;
  quantity: number;
  unitPrice: number;
  unit: string;
}

interface Category {
  id: number;
  name: string;
  count: number;
}

interface CartItem {
  product: Product;
  quantity: number;
}

interface ProductSelectorProps {
  products: Product[];
  note: string;
  onRequestQuote: (items: SelectedItem[]) => void;
}

// ── API helpers ──

const API_BASE = `${import.meta.env.VITE_API_URL || ''}/api/v1`;

async function fetchProducts(query: string, categoryId?: number): Promise<Product[]> {
  const params = new URLSearchParams({ q: query, limit: '10' });
  if (categoryId) params.set('category', String(categoryId));
  try {
    const res = await fetch(`${API_BASE}/products/search?${params}`);
    if (!res.ok) return [];
    const data = await res.json();
    return data.products || [];
  } catch {
    return [];
  }
}

async function fetchCategories(): Promise<Category[]> {
  try {
    const res = await fetch(`${API_BASE}/products/categories`);
    if (!res.ok) return [];
    const data = await res.json();
    return data.categories || [];
  } catch {
    return [];
  }
}

// ── Helpers ──

function formatPrice(value: number): string {
  return value.toLocaleString('vi-VN') + 'đ';
}

// ── Component ──

export const ProductSelector: React.FC<ProductSelectorProps> = ({ products, note, onRequestQuote }) => {
  const [activeTab, setActiveTab] = useState<'search' | 'cart'>('search');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
  const [searchResults, setSearchResults] = useState<Product[]>(products);
  const [categories, setCategories] = useState<Category[]>([]);
  const [cart, setCart] = useState<Map<number, CartItem>>(new Map());
  const [searching, setSearching] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Fetch categories on mount
  useEffect(() => {
    fetchCategories().then(setCategories);
  }, []);

  // Debounced search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    // If no query and no category filter, show initial products
    if (!searchQuery.trim() && selectedCategory === null) {
      setSearchResults(products);
      setSearching(false);
      return;
    }

    // API requires minimum 2 chars — don't fetch with empty/short query
    if (searchQuery.trim().length < 2 && selectedCategory === null) {
      setSearchResults(products);
      setSearching(false);
      return;
    }

    setSearching(true);
    debounceRef.current = setTimeout(async () => {
      // When category selected but query empty, search with category name as fallback
      const query = searchQuery.trim().length >= 2 ? searchQuery.trim() : '';
      if (!query && !selectedCategory) { setSearchResults(products); setSearching(false); return; }
      const results = await fetchProducts(
        query || '*',
        selectedCategory ?? undefined,
      );
      setSearchResults(results);
      setSearching(false);
    }, 300);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchQuery, selectedCategory, products]);

  // Cart helpers
  const addToCart = useCallback((product: Product) => {
    setCart((prev) => {
      const next = new Map(prev);
      if (!next.has(product.id)) {
        next.set(product.id, { product, quantity: 1 });
      }
      return next;
    });
  }, []);

  const removeFromCart = useCallback((productId: number) => {
    setCart((prev) => {
      const next = new Map(prev);
      next.delete(productId);
      return next;
    });
  }, []);

  const updateQuantity = useCallback((productId: number, value: string) => {
    const qty = Math.max(0, parseInt(value) || 0);
    setCart((prev) => {
      const next = new Map(prev);
      const item = next.get(productId);
      if (item) {
        next.set(productId, { ...item, quantity: qty });
      }
      return next;
    });
  }, []);

  const total = useMemo(() => {
    let sum = 0;
    cart.forEach((item) => {
      sum += item.product.price * item.quantity;
    });
    return sum;
  }, [cart]);

  const handleRequestQuote = useCallback(() => {
    const items: SelectedItem[] = Array.from(cart.values())
      .filter((item) => item.quantity > 0)
      .map((item) => ({
        name: item.product.name,
        code: item.product.code,
        quantity: item.quantity,
        unitPrice: item.product.price,
        unit: item.product.unit,
      }));
    onRequestQuote(items);
  }, [cart, onRequestQuote]);

  const cartSize = cart.size;

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm max-w-[380px] overflow-hidden">
      {/* Header */}
      <div className="px-3 py-2.5 border-b border-gray-200">
        <p className="text-sm font-medium text-gray-700 mb-2">Tra gia san pham</p>

        {/* Search input */}
        <div className="relative mb-2">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Tim kiem san pham..."
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-red-500 focus:border-red-500 pr-8"
          />
          {searching && (
            <div className="absolute right-2.5 top-1/2 -translate-y-1/2">
              <div className="w-4 h-4 border-2 border-gray-300 border-t-red-500 rounded-full animate-spin" />
            </div>
          )}
        </div>

        {/* Category select */}
        {categories.length > 0 && (
          <select
            value={selectedCategory ?? ''}
            onChange={(e) => setSelectedCategory(e.target.value ? Number(e.target.value) : null)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-red-500 focus:border-red-500 bg-white"
          >
            <option value="">Tat ca danh muc</option>
            {categories.map((cat) => (
              <option key={cat.id} value={cat.id}>
                {cat.name} ({cat.count})
              </option>
            ))}
          </select>
        )}
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200">
        <button
          onClick={() => setActiveTab('search')}
          className={`flex-1 py-2 text-sm font-medium transition-colors ${
            activeTab === 'search'
              ? 'text-red-600 border-b-2 border-red-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Tim kiem
        </button>
        <button
          onClick={() => setActiveTab('cart')}
          className={`flex-1 py-2 text-sm font-medium transition-colors ${
            activeTab === 'cart'
              ? 'text-red-600 border-b-2 border-red-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Gio hang{cartSize > 0 ? ` (${cartSize})` : ''}
        </button>
      </div>

      {/* Tab content */}
      <div className="max-h-[240px] overflow-y-auto">
        {activeTab === 'search' ? (
          /* Search results tab */
          searchResults.length === 0 ? (
            <div className="px-3 py-6 text-center text-sm text-gray-400">
              {searching ? 'Dang tim kiem...' : 'Khong tim thay san pham'}
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {searchResults.map((product) => {
                const inCart = cart.has(product.id);
                return (
                  <div key={product.id} className="px-3 py-2.5">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-800 truncate">{product.name}</p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          <span className="font-medium text-gray-700">{formatPrice(product.price)}</span>
                          /{product.unit}
                          {product.weight > 0 && (
                            <span className="ml-1.5">| {product.weight}kg</span>
                          )}
                        </p>
                      </div>
                      <button
                        onClick={() => addToCart(product)}
                        disabled={inCart}
                        className={`shrink-0 text-sm rounded px-2 py-0.5 transition-colors ${
                          inCart
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'bg-red-50 text-red-600 hover:bg-red-100'
                        }`}
                      >
                        {inCart ? 'Da them' : '+ Them'}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )
        ) : (
          /* Cart tab */
          cartSize === 0 ? (
            <div className="px-3 py-6 text-center text-sm text-gray-400">
              Gio hang trong
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {Array.from(cart.entries()).map(([productId, item]) => (
                <div key={productId} className="px-3 py-2.5">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-800 truncate">{item.product.name}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs text-gray-500">
                          <span className="font-medium text-gray-700">{formatPrice(item.product.price)}</span>
                          /{item.product.unit}
                        </span>
                        <span className="text-xs text-gray-400">SL:</span>
                        <input
                          type="number"
                          min={0}
                          value={item.quantity || ''}
                          onChange={(e) => updateQuantity(productId, e.target.value)}
                          placeholder="0"
                          className="w-16 text-center text-sm border border-gray-300 rounded px-1 py-0.5 focus:outline-none focus:ring-1 focus:ring-red-500 focus:border-red-500"
                          aria-label={`So luong ${item.product.name}`}
                        />
                      </div>
                    </div>
                    <button
                      onClick={() => removeFromCart(productId)}
                      className="shrink-0 text-gray-400 hover:text-red-500 transition-colors p-1"
                      aria-label={`Xoa ${item.product.name}`}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )
        )}
      </div>

      {/* Footer */}
      <div className="px-3 py-3 border-t border-gray-200 space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-600">Tong:</span>
          <span className="text-lg font-semibold text-gray-800">{formatPrice(total)}</span>
        </div>
        <p className="text-xs text-gray-400">(chua VAT)</p>

        <button
          onClick={handleRequestQuote}
          disabled={cartSize === 0 || !Array.from(cart.values()).some((i) => i.quantity > 0)}
          className="w-full bg-red-600 hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg py-2 transition-colors"
        >
          Yeu cau bao gia
        </button>

        {note && (
          <p className="text-xs text-gray-400 italic text-center">{note.slice(0, 200)}</p>
        )}
      </div>
    </div>
  );
};
